import { CONFIGURATION_STATUS_TYPE } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Option } from 'src/shared/form/control'
import React, { useCallback, useMemo } from 'react'
import { Form } from 'react-final-form'
import { Alert, Box, Grid2, Stack, Typography } from '@mui/material'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { CheckboxGroupField } from 'src/shared/form/control/CheckboxGroupField'
import { required } from 'src/shared/form/validation/validators'
import { TextField } from 'src/shared/form/control/TextField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { getConfigStatusOptions } from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { AddButton } from 'src/shared/button/Buttons'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM } from 'src/screens/administration/configuration-management/help-instructions-configuration/HelpInstructionsGridUtils'

export interface FeatureTypesConfigFormValues {
  enName: string
  deName: string
  frName: string
  itName: string
  enDescription: string
  deDescription: string
  frDescription: string
  itDescription: string
  status: CONFIGURATION_STATUS_TYPE
  hasKapFactsheetValidation: boolean
  helpAndInstructionsKey: string
}

interface FeatureTypesConfigFormProps {
  mode: 'CREATE' | 'EDIT'
  onSubmit: (values: FeatureTypesConfigFormValues) => Promise<void>
  initialValues: FeatureTypesConfigFormValues
  formDirty?: boolean
}

export const FeaturesConfigurationFeatureTypesForm = ({
  onSubmit,
  initialValues,
  formDirty,
  mode,
}: FeatureTypesConfigFormProps) => {
  const { getMessage } = useMessageSource()
  const decorators = createDecorators()
  const navigate = useDelayedNavigate()

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])

  const handleAddHelpInstructionsLink = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, helpAndInstructionsKey: string) => {
      e.stopPropagation()

      const searchParamsString = `${HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM}=${helpAndInstructionsKey}`
      navigate(`${ROUTES.HelpInstructionsConfigurationCreate.params({})}?${searchParamsString}`)
    },
    [navigate],
  )

  return (
    <Stack spacing={2}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<FeatureTypesConfigFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        decorators={decorators}
        render={({ handleSubmit }) => {
          return (
            <form id="feature-types-config-form" onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <CheckboxGroupField
                      name="hasKapFactsheetValidation"
                      data={{
                        label: getMessage('label.configuration.features.type.checkbox.validation.kap.factsheet'),
                        value: true,
                      }}
                      color="primary"
                      required
                      formControlProps={{ style: { marginTop: 5 } }}
                      $noLabel
                      $paddingInline={0}
                    />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.configuration.features.feature.type.name')}</Typography>

                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <TextField name="deName" label="DE" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enName" label="EN" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frName" label="FR" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itName" label="IT" required validate={required()} />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">
                  {getMessage('label.configuration.features.type.tooltip.description')}
                </Typography>

                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <TextField name="deDescription" label="DE" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enDescription" label="EN" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frDescription" label="FR" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itDescription" label="IT" required validate={required()} />
                  </Grid2>
                </Grid2>
                <DirtyFormSpy gridDirty={formDirty} />
              </Stack>
            </form>
          )
        }}
      />
      {mode === 'EDIT' && (
        <Box display="flex" justifyContent="start">
          <AddButton
            messageKey={'label.configuration.features.feature.type.add.help.instructions.link.button'}
            startIcon={<AddOutlinedIcon />}
            onClick={(e) => handleAddHelpInstructionsLink(e, initialValues.helpAndInstructionsKey)}
          />
        </Box>
      )}
    </Stack>
  )
}
